import { useQuery } from '@tanstack/react-query';
import type { ScormPackageConfig, ScormVersion } from '@course-authoring/shared-types';
import { fetchWithAuthRetry, handleApiResponse } from './apiUtils';
import { blobToBase64 } from './courseDeployApi';
import { buildQuestionnairePackageZipBlob, buildScormZipBlob } from './packageBundler';
import type { PackageLiveStatus } from './publishApi';
import { toScopeAccountId } from '../hooks/useClientContext';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export const questionnaireKeys = {
  all: ['assignedQuestionnaires'] as const,
  forAccount: (accountId?: string) => [...questionnaireKeys.all, accountId ?? 'base'] as const,
};

export type QuestionnaireLifecycle = 'draft' | 'review' | 'publish' | 'close' | 'retired';

export interface AssignedQuestionnaire {
  id: string;
  title: string;
  /** DNS-safe account slug returned by the endpoint — do NOT derive from clientContext.subdomain. */
  accountSlug: string;
  lifecycle: QuestionnaireLifecycle;
  /**
   * The authored revision (hoot's `questionnaires.updatedAt`). Fed to the
   * delivery badge as its content-publish time, so a questionnaire edited in
   * wh-frontend since its last publish reads as stale rather than live.
   * Optional: absent when talking to a hoot that predates the field.
   */
  updatedAt?: string;
}

export type QuestionnaireErrorCode =
  | 'UNRESOLVED_TERMINALS'
  | 'MISSING_SCORM_CREDENTIALS'
  /** Download SCORM before Publish — nothing is hosted to point the shell at. */
  | 'NOT_DEPLOYED'
  | 'UNKNOWN';

/** One terminal the export could not resolve to a published package. */
export interface UnresolvedTerminal {
  nodeId: string;
  resourceName: string | null;
  reason: string;
}

export class QuestionnaireApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: QuestionnaireErrorCode,
    /**
     * Present on UNRESOLVED_TERMINALS. Carried so the page can name the content to
     * publish — "node 7 is unresolved" is not something an author can act on.
     */
    public readonly terminals?: UnresolvedTerminal[],
    /**
     * Also present on UNRESOLVED_TERMINALS, for readers that want to REPORT the
     * refusal rather than act on it: the readiness panel lists every terminal a
     * questionnaire assigns, which needs the inventory as well as the failures.
     * Absent against an author-api that predates it, so treat it as optional.
     */
    public readonly document?: QuestionnaireDocView,
    /**
     * Facts about the terminals that ARE fine, carried on the refusal for the same
     * reason as `document`: a panel that only rendered failures could say nothing
     * about the rest of the questionnaire.
     */
    public readonly terminalInfo?: TerminalInfoView[],
  ) {
    super(message);
    this.name = 'QuestionnaireApiError';
  }
}

/**
 * The little of the baked document this app reads.
 *
 * Deliberately NOT the player's full `QuestionnaireDoc`: publishing passes documents
 * through opaquely so a field added server-side needs no change here, and that stays
 * true. This is a narrow view for one screen — four fields, and only for display.
 */
export interface QuestionnaireDocNodeView {
  id: string;
  nodeType: 'question' | 'assignment' | 'no-assignment';
  resourceName?: string | null;
  contentType?: 'programs' | 'microlessons';
  contentId?: string;
  /** The Author Platform id (authorAICourses.id / authorAILessons.id) behind this terminal. */
  authorAiCourseId?: string;
  /**
   * Question nodes only. Read for one purpose: finding answers that lead nowhere. The
   * graph is already on the wire, so the alternative would be asking the server to
   * compute a display concern it has no other use for.
   */
  questionText?: string;
  answers?: Array<{ text?: string; nextNodeId?: string | null }>;
  /** Where answers without a child of their own lead. Null means nowhere. */
  defaultNextNodeId?: string | null;
}

/** An answer a learner can pick that leads to no next step at all. */
export interface DeadEndBranch {
  nodeId: string;
  questionText: string | null;
  choiceText: string | null;
}

/**
 * Answers that leave the learner with nowhere to go.
 *
 * A choice with no child of its own falls back to the question's default branch. When
 * there is no default either, the learner is shown "no training assigned" and — unlike an
 * explicit no-assignment OUTCOME — nothing is reported to hoot. So no completion is
 * recorded, and on relaunch they are asked every question again. Nobody can see this
 * today: wh-frontend draws the tree but not the defect.
 */
const findDeadEnds = (nodes: QuestionnaireDocNodeView[]): DeadEndBranch[] => {
  const deadEnds: DeadEndBranch[] = [];
  for (const node of nodes) {
    if (node.nodeType !== 'question') continue;
    // An absent default is the condition; `undefined` from an older server is treated the
    // same as null, because both mean "no default branch was baked".
    if (node.defaultNextNodeId) continue;
    for (const answer of node.answers ?? []) {
      if (answer.nextNodeId) continue;
      deadEnds.push({
        nodeId: node.id,
        questionText: node.questionText ?? null,
        choiceText: answer.text ?? null,
      });
    }
  }
  return deadEnds;
};

export interface QuestionnaireDocView {
  nodes: QuestionnaireDocNodeView[];
}

/**
 * Per-terminal FACTS, as opposed to findings.
 *
 * The server reports problems in `warnings` / `terminals`, so a healthy resource has no
 * entry there at all and nothing can be said about it. This carries what is true of a
 * resource regardless of whether anything is wrong with it.
 *
 * Every field is optional and absent means UNKNOWN — never zero, never a negative.
 */
export interface TerminalInfoView {
  nodeId: string;
  /** When the content behind the deployed package was published. */
  publishedAt?: string;
}

const normalizeTerminalInfo = (value: unknown): TerminalInfoView[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
    .map((entry) => ({
      nodeId: typeof entry.nodeId === 'string' ? entry.nodeId : '',
      ...(typeof entry.publishedAt === 'string' ? { publishedAt: entry.publishedAt } : {}),
    }))
    .filter((entry) => entry.nodeId !== '');
};

const isDocView = (value: unknown): value is QuestionnaireDocView =>
  typeof value === 'object' && value !== null && Array.isArray((value as QuestionnaireDocView).nodes);

export async function fetchAssignedQuestionnaires(accountId?: string): Promise<AssignedQuestionnaire[]> {
  const scope = toScopeAccountId(accountId);
  const res = await fetchWithAuthRetry(`${API_BASE_URL}/questionnaires/assigned?accountId=${scope ?? ''}`);
  const rows = await handleApiResponse<AssignedQuestionnaire[]>(
    res,
    'Failed to load assigned questionnaires',
  );
  // hoot returns a NUMERIC questionnaire id and author-api passes the payload
  // through verbatim, so the declared `string` was a lie at runtime. Normalized
  // here rather than at each use site (React keys, export filenames, and the
  // SCORM zip's courseId all expect a string).
  return rows.map((row) => ({ ...row, id: String(row.id) }));
}

// Used by both the page and the sidebar visibility check.
export function useAssignedQuestionnaires(accountId?: string) {
  return useQuery({
    queryKey: questionnaireKeys.forAccount(accountId),
    queryFn: () => fetchAssignedQuestionnaires(accountId),
    enabled: !!accountId, // don't fetch for Standard (null clientContext)
    // Overrides the app-wide 5-minute staleTime: this row carries the authored
    // CONTENT revision, which the delivery badge compares against the deployed
    // package's. Served from a stale cache, an edit made in wh-frontend would keep
    // reading as "live" for up to five minutes — the badge would be confidently
    // wrong about exactly the thing it exists to catch.
    staleTime: 0,
  });
}

export function useHasAssignedQuestionnaires(accountId?: string): boolean {
  const query = useAssignedQuestionnaires(accountId);
  return (query.data?.length ?? 0) > 0;
}

/** The default-locale document's package path — author-api decides the layout. */
const QUESTIONNAIRE_DEFAULT_DOC_PATH = 'data/questionnaire.json';

async function parseQuestionnaireError(response: Response, fallback: string): Promise<QuestionnaireApiError> {
  let message = fallback;
  let code: QuestionnaireErrorCode = 'UNKNOWN';
  let terminals: UnresolvedTerminal[] | undefined;
  let document: QuestionnaireDocView | undefined;
  let terminalInfo: TerminalInfoView[] | undefined;
  try {
    const parsed = (await response.json()) as {
      message?: unknown;
      code?: unknown;
      terminals?: unknown;
      document?: unknown;
      terminalInfo?: unknown;
    };
    if (typeof parsed.message === 'string') message = parsed.message;
    if (typeof parsed.code === 'string') code = parsed.code as QuestionnaireErrorCode;
    if (Array.isArray(parsed.terminals)) {
      terminals = parsed.terminals
        .filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
        .map((entry) => ({
          nodeId: typeof entry.nodeId === 'string' ? entry.nodeId : '',
          resourceName: typeof entry.resourceName === 'string' ? entry.resourceName : null,
          reason: typeof entry.reason === 'string' ? entry.reason : '',
        }));
    }
    if (isDocView(parsed.document)) document = parsed.document;
    const info = normalizeTerminalInfo(parsed.terminalInfo);
    if (info.length > 0) terminalInfo = info;
  } catch {
    // Non-JSON error body; keep the status-based fallback message.
  }
  return new QuestionnaireApiError(message, response.status, code, terminals, document, terminalInfo);
}

// ─── SCORM export (NA-256) ────────────────────────────────────────────────────
//
// Two independent actions, mirroring the course flow:
//
//   Publish        → build the questionnaire content package and host it
//   Download SCORM → build the thin RXD shell for the ALREADY-hosted package
//
// Neither does the other's work. Publishing produces no download, downloading
// re-hosts nothing (so a shell already installed in an LMS keeps working across
// republishes — it points at `…/current/`), and downloading before publishing is
// refused rather than producing a shell aimed at an empty prefix.

/** The RXD shell's inputs for an already-hosted questionnaire package. */
interface QuestionnaireExportPackageInfo {
  accountSlug: string;
  questionnairePackageUrl: string;
  externalAccountId: number;
  scormVersion?: string;
  apiKey: string;
  integrationKey: string;
  host: string;
  apiBaseUrl?: string;
}

/**
 * `{Title}_Q{id}_SCORM_{version}_{YYYYMMDD}.zip`, following the course/lesson
 * convention.
 *
 * Not `publishApi.buildScormExportFilename`: that helper formats a `C{n}`/`L{n}`
 * badge from an `authorAICourses`/`authorAILessons` id, and its
 * `ScormExportContext` cannot express `'questionnaire'`. A questionnaire has no
 * such badge, so routing it through there would label it as a course.
 */
function buildQuestionnaireScormFilename(
  questionnaire: AssignedQuestionnaire,
  scormVersion: ScormVersion,
  downloadedAt: Date = new Date(),
): string {
  const cleanTitle =
    questionnaire.title.replace(/\W+/g, '_').replace(/^_+|_+$/g, '') || `questionnaire_${questionnaire.id}`;
  const stamp =
    `${downloadedAt.getFullYear()}` +
    `${String(downloadedAt.getMonth() + 1).padStart(2, '0')}` +
    `${String(downloadedAt.getDate()).padStart(2, '0')}`;
  return `${cleanTitle}_Q${questionnaire.id}_SCORM_${scormVersion}_${stamp}.zip`;
}

/** Mirrors publishApi's course-side normalization. */
function normalizeScormVersion(raw: string | undefined): ScormVersion {
  const value = (raw ?? '').toLowerCase();
  if (value.includes('4th')) return '2004_4th';
  if (value.includes('2004')) return '2004_3rd';
  return '1.2';
}

/** Non-blocking findings from the terminal check, reported alongside the document. */
export interface QuestionnaireWarning {
  nodeId: string;
  resourceName: string | null;
  reason: string;
}

async function fetchQuestionnaireDoc(
  questionnaire: AssignedQuestionnaire,
): Promise<{
  documents: Record<string, unknown>;
  warnings: QuestionnaireWarning[];
  terminalInfo: TerminalInfoView[];
}> {
  const res = await fetchWithAuthRetry(
    `${API_BASE_URL}/questionnaires/${questionnaire.id}/questionnaire-json` +
      `?accountSlug=${encodeURIComponent(questionnaire.accountSlug)}`,
  );
  if (!res.ok) {
    throw await parseQuestionnaireError(res, `Failed to prepare the questionnaire package (${res.status})`);
  }
  // Documents are passed through opaquely, keyed by the package-relative path each
  // must be written to — author-api owns both the shape and the file layout, and the
  // player reads them, so a field added server-side needs no change here.
  const body = (await res.json()) as {
    questionnaire?: unknown;
    documents?: Record<string, unknown>;
    warnings?: QuestionnaireWarning[];
    terminalInfo?: unknown;
  };
  const warnings = Array.isArray(body.warnings) ? body.warnings : [];
  const terminalInfo = normalizeTerminalInfo(body.terminalInfo);

  if (body.documents && Object.keys(body.documents).length > 0) {
    return { documents: body.documents, warnings, terminalInfo };
  }
  // An author-api that predates translations returns only `questionnaire`. Writing it
  // to the default path keeps publishing working against a not-yet-deployed server
  // rather than failing on a field that used to be absent.
  if (body.questionnaire) {
    return {
      documents: { [QUESTIONNAIRE_DEFAULT_DOC_PATH]: body.questionnaire },
      warnings,
      terminalInfo,
    };
  }
  throw new QuestionnaireApiError('Response carried no questionnaire document.', res.status, 'UNKNOWN');
}

async function fetchQuestionnaireExportPackageInfo(
  questionnaire: AssignedQuestionnaire,
): Promise<QuestionnaireExportPackageInfo> {
  const res = await fetchWithAuthRetry(
    `${API_BASE_URL}/questionnaires/${questionnaire.id}/export-package-info` +
      `?accountSlug=${encodeURIComponent(questionnaire.accountSlug)}`,
  );
  if (!res.ok) {
    throw await parseQuestionnaireError(res, `Failed to resolve the hosted package (${res.status})`);
  }
  return (await res.json()) as QuestionnaireExportPackageInfo;
}

/**
 * Delivery check for a questionnaire package — the "Live on CDN" badge's source.
 *
 * Shares the server's verdict core with courses, so `PackageLiveStatus` and every
 * status `CdnLiveStatus` renders apply unchanged. `deployedAt` is the extra field
 * questionnaires need: with no author-api publish row, the deploy stamp IS the
 * publish time.
 */
export async function fetchQuestionnaireLiveStatus(
  questionnaireId: string,
  accountSlug?: string,
): Promise<PackageLiveStatus & { deployedAt?: string }> {
  const query = accountSlug ? `?accountSlug=${encodeURIComponent(accountSlug)}` : '';
  const res = await fetchWithAuthRetry(`${API_BASE_URL}/questionnaires/${questionnaireId}/live-status${query}`);
  if (res.status === 404) {
    return { deployed: false, live: false, checkedAt: new Date().toISOString() };
  }
  if (!res.ok) {
    throw await parseQuestionnaireError(res, `Delivery check failed (${res.status})`);
  }
  return (await res.json()) as PackageLiveStatus & { deployedAt?: string };
}

export function useQuestionnaireLiveStatus(questionnaire: AssignedQuestionnaire, enabled: boolean) {
  return useQuery({
    queryKey: [...questionnaireKeys.all, 'live', questionnaire.id, questionnaire.accountSlug],
    queryFn: () => fetchQuestionnaireLiveStatus(questionnaire.id, questionnaire.accountSlug),
    enabled,
    // The badge owns polling; this read only needs to be fresh when the row mounts
    // or after a publish invalidates it.
    staleTime: 0,
  });
}

/**
 * Publish — build the questionnaire content package in the browser (the player
 * bundle is this app's own build artifact) and host it under the account's
 * package prefix. Returns the hosted URL.
 */
export interface QuestionnairePublishTransport {
  prepare: () => Promise<{ documents: Record<string, unknown>; warnings?: QuestionnaireWarning[] }>;
  deploy: (packageBase64: string) => Promise<Response>;
}

export async function publishQuestionnaire(
  questionnaire: AssignedQuestionnaire,
  transport?: QuestionnairePublishTransport,
): Promise<{ questionnairePackageUrl: string; warnings: QuestionnaireWarning[] }> {
  const { documents, warnings = [] } = await (transport ? transport.prepare() : fetchQuestionnaireDoc(questionnaire));
  const packageBlob = await buildQuestionnairePackageZipBlob(documents);
  const packageBase64 = await blobToBase64(packageBlob);

  const res = await (transport ? transport.deploy(packageBase64) : fetchWithAuthRetry(`${API_BASE_URL}/questionnaires/${questionnaire.id}/deploy`, {
    method: 'POST',
    body: JSON.stringify({ packageBase64, accountSlug: questionnaire.accountSlug }),
  }));
  if (!res.ok) {
    throw await parseQuestionnaireError(res, `Failed to publish the questionnaire package (${res.status})`);
  }
  const body = (await res.json()) as { questionnairePackageUrl?: unknown; warnings?: unknown };
  if (typeof body.questionnairePackageUrl !== 'string' || !body.questionnairePackageUrl) {
    throw new QuestionnaireApiError('Publish returned no hosted package URL.', res.status, 'UNKNOWN');
  }
  // Deploy-side degradations (branding assets that failed to bundle) join the
  // terminal findings — the package shipped, but not entirely as intended.
  const deployWarnings = Array.isArray(body.warnings)
    ? body.warnings
      .filter((entry): entry is string => typeof entry === 'string')
      .map((reason) => ({ nodeId: '', resourceName: null, reason }))
    : [];
  return {
    questionnairePackageUrl: body.questionnairePackageUrl,
    warnings: [...warnings, ...deployWarnings],
  };
}

/**
 * Download SCORM — the thin RXD shell for the LMS, pointing at the hosted
 * package. Builds nothing else and deploys nothing: if the questionnaire has not
 * been published for this account, author-api answers `NOT_DEPLOYED` and no shell
 * is produced.
 */
export async function downloadQuestionnaireScorm(questionnaire: AssignedQuestionnaire): Promise<void> {
  const info = await fetchQuestionnaireExportPackageInfo(questionnaire);

  const scormVersion = normalizeScormVersion(info.scormVersion);
  const scorm: ScormPackageConfig = {
    coursePackageUrl: info.questionnairePackageUrl,
    apiKey: info.apiKey,
    integrationKey: info.integrationKey,
    enforceSequence: false,
  };
  const shellBlob = await buildScormZipBlob({
    courseId: `questionnaire-${questionnaire.id}`,
    courseTitle: questionnaire.title,
    scormVersion,
    scorm,
  });

  const url = URL.createObjectURL(shellBlob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = buildQuestionnaireScormFilename(questionnaire, scormVersion);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// ─── Readiness (authoring only) ───────────────────────────────────────────────
//
// "Which resources does this questionnaire assign, and is each one published?"
// Nothing else answers that: publish reports its findings in a banner that is
// cleared on the next action, and the delivery badge describes the questionnaire's
// own package rather than the courses it points at.
//
// Reads the SAME endpoint publish reads, and treats BOTH outcomes as data:
//   200 → the document (the inventory) + `warnings` (advisory findings)
//   409 → the document, when the server sends it, + `terminals` (blocking findings)
// The status code is what keeps publish safe — it still throws on 409 and never
// looks at the document — so this needs no endpoint of its own.
//
// COST: one hoot tree fetch, ~2 storage reads per distinct terminal, publish-state
// queries and a pointer fan-out. Never call this on render for a list of rows — the
// hook below is `enabled: false` until something explicitly asks.

export type TerminalReadinessState = 'ready' | 'advisory' | 'blocking';

export interface TerminalReadiness {
  nodeId: string;
  /** Null for a no-assignment outcome, and for a finding whose content has no name. */
  resourceName: string | null;
  contentType?: 'programs' | 'microlessons';
  contentId?: string;
  /** The Author Platform id (authorAICourses.id / authorAILessons.id) behind this terminal. */
  authorAiCourseId?: string;
  state: TerminalReadinessState;
  /** When the content behind this terminal's deployed package was published. */
  publishedAt?: string;
  /**
   * Empty for a ready terminal; the server's own wording otherwise, one entry per
   * distinct problem.
   *
   * A list rather than a single string because one resource can be wrong in more than
   * one way at once — republished but not deployed here, AND edited again since — and
   * the remedies differ (a deploy versus a publish then a deploy). Keying findings by
   * node and keeping only the last would drop whichever the server happened to report
   * first.
   */
  reasons: string[];
}

export interface QuestionnaireReadiness {
  /** Assigned resources, blocking first — the order an author needs to act in. */
  terminals: TerminalReadiness[];
  /** Branches that deliberately assign nothing. */
  noAssignmentOutcomes: number;
  questionCount: number;
  /** True when the questionnaire cannot be published as it stands. */
  blocked: boolean;
  /**
   * True when the server refused without sending a document — an author-api that
   * predates the field. Findings are still listed; the inventory is not available.
   */
  inventoryUnavailable: boolean;
  /** Answers that lead nowhere. Empty when the graph is sound OR was not supplied. */
  deadEnds: DeadEndBranch[];
  checkedAt: string;
}

const summarize = (
  document: QuestionnaireDocView | undefined,
  findings: Array<{ nodeId: string; resourceName: string | null; reason: string }>,
  state: Exclude<TerminalReadinessState, 'ready'>,
  terminalInfo: TerminalInfoView[] = [],
): QuestionnaireReadiness => {
  const nodes = document?.nodes ?? [];
  // Keyed, unlike findings: the server emits at most one info entry per node, because
  // it describes the node rather than something that happened to it.
  const infoByNode = new Map(terminalInfo.map((entry) => [entry.nodeId, entry]));
  // Grouped, not keyed: a node can carry several findings at once, and a Map of
  // nodeId → finding would keep only whichever arrived last.
  const findingsByNode = new Map<string, typeof findings>();
  for (const finding of findings) {
    const existing = findingsByNode.get(finding.nodeId);
    if (existing) existing.push(finding);
    else findingsByNode.set(finding.nodeId, [finding]);
  }

  const terminals: TerminalReadiness[] = nodes
    .filter((node) => node.nodeType === 'assignment')
    .map((node) => {
      const nodeFindings = findingsByNode.get(node.id) ?? [];
      findingsByNode.delete(node.id);
      return {
        nodeId: node.id,
        resourceName: node.resourceName ?? null,
        ...(node.contentType ? { contentType: node.contentType } : {}),
        ...(node.contentId ? { contentId: node.contentId } : {}),
        ...(node.authorAiCourseId ? { authorAiCourseId: node.authorAiCourseId } : {}),
        state: nodeFindings.length > 0 ? state : ('ready' as const),
        reasons: nodeFindings.map((finding) => finding.reason),
        ...(infoByNode.get(node.id)?.publishedAt
          ? { publishedAt: infoByNode.get(node.id)!.publishedAt }
          : {}),
      };
    });

  // A blocking finding names a node the document omitted (an unusable terminal is
  // left out of the baked nodes), so what remains after the join still has to be
  // reported — those are precisely the ones the author must fix.
  for (const [nodeId, nodeFindings] of findingsByNode) {
    terminals.push({
      nodeId,
      resourceName: nodeFindings[0]?.resourceName ?? null,
      state,
      reasons: nodeFindings.map((finding) => finding.reason),
    });
  }

  const order: Record<TerminalReadinessState, number> = { blocking: 0, advisory: 1, ready: 2 };
  terminals.sort((left, right) => order[left.state] - order[right.state]);

  return {
    terminals,
    noAssignmentOutcomes: nodes.filter((node) => node.nodeType === 'no-assignment').length,
    questionCount: nodes.filter((node) => node.nodeType === 'question').length,
    blocked: state === 'blocking' && terminals.some((terminal) => terminal.state === 'blocking'),
    inventoryUnavailable: nodes.length === 0,
    deadEnds: findDeadEnds(nodes),
    checkedAt: new Date().toISOString(),
  };
};

export async function fetchQuestionnaireReadiness(
  questionnaire: AssignedQuestionnaire,
): Promise<QuestionnaireReadiness> {
  try {
    const { documents, warnings, terminalInfo } = await fetchQuestionnaireDoc(questionnaire);
    const document = documents[QUESTIONNAIRE_DEFAULT_DOC_PATH];
    return summarize(isDocView(document) ? document : undefined, warnings, 'advisory', terminalInfo);
  } catch (error) {
    // A refusal is an answer here, not a failure: it says which terminals are
    // unpublished. Anything else (auth, network, a misconfigured server) is a real
    // error and stays one.
    if (error instanceof QuestionnaireApiError && error.code === 'UNRESOLVED_TERMINALS') {
      return summarize(error.document, error.terminals ?? [], 'blocking', error.terminalInfo ?? []);
    }
    throw error;
  }
}

/**
 * Deliberately inert until `enabled` — see the COST note above. The result is
 * cached per questionnaire, so re-opening the panel costs nothing for the session.
 */
export function useQuestionnaireReadiness(questionnaire: AssignedQuestionnaire, enabled: boolean) {
  return useQuery({
    queryKey: [...questionnaireKeys.all, 'readiness', questionnaire.id] as const,
    queryFn: () => fetchQuestionnaireReadiness(questionnaire),
    enabled,
    staleTime: 60_000,
  });
}
